import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import {
  MigrationApiAdapter,
  type MigrationReconciliationCheckKey,
  type MigrationScope,
  type MigrationProgressResponse,
} from '../adapters/MigrationApiAdapter';

const MIGRATION_QUERY_KEYS = {
  children: ['migration', 'children'] as const,
  run: (childTeamId: number) => ['migration', 'run', childTeamId] as const,
  progress: (runId: number) => ['migration', 'progress', runId] as const,
  errors: (runId: number, params?: Record<string, unknown>) => ['migration', 'errors', runId, params] as const,
  activity: (runId: number, params?: Record<string, unknown>) => ['migration', 'activity', runId, params] as const,
  reconciliation: (runId: number, checks?: string[]) => ['migration', 'reconciliation', runId, checks] as const,
};

export const useMigrationChildren = () => {
  const apiAdapter = useMemo(() => {
    const httpClient = createHttpClient(apiClientWH);
    return new MigrationApiAdapter(httpClient);
  }, []);

  return useQuery({
    queryKey: MIGRATION_QUERY_KEYS.children,
    queryFn: () => apiAdapter.getChildren(),
    staleTime: 30 * 1000,
  });
};

export const useMigrationRun = (childTeamId: number, enabled = true) => {
  const apiAdapter = useMemo(() => {
    const httpClient = createHttpClient(apiClientWH);
    return new MigrationApiAdapter(httpClient);
  }, []);

  return useQuery({
    queryKey: MIGRATION_QUERY_KEYS.run(childTeamId),
    queryFn: () => apiAdapter.getRun(childTeamId),
    enabled: enabled && !!childTeamId,
    staleTime: 10 * 1000,
  });
};

export const useMigrationProgress = (
  runId: number | null,
  options?: { refetchInterval?: number; enabled?: boolean },
) => {
  const apiAdapter = useMemo(() => {
    const httpClient = createHttpClient(apiClientWH);
    return new MigrationApiAdapter(httpClient);
  }, []);

  const enabled = options?.enabled ?? !!runId;
  const refetchInterval = options?.refetchInterval ?? 5000;

  return useQuery({
    queryKey: MIGRATION_QUERY_KEYS.progress(runId ?? 0),
    queryFn: () => apiAdapter.getProgress(runId!),
    enabled: enabled && !!runId,
    refetchInterval: (query) => {
      const data = query.state.data as MigrationProgressResponse | undefined;
      const isActive = data?.migration_run?.status === 'running' || data?.migration_run?.status === 'paused';
      return isActive ? refetchInterval : false;
    },
    staleTime: 2000,
  });
};

export const useMigrationStart = () => {
  const queryClient = useQueryClient();
  const apiAdapter = useMemo(() => {
    const httpClient = createHttpClient(apiClientWH);
    return new MigrationApiAdapter(httpClient);
  }, []);

  return useMutation({
    mutationFn: ({
      childTeamId,
      externalTeamId,
      stockFile,
      scope,
    }: {
      childTeamId: number;
      externalTeamId: string;
      stockFile?: File;
      scope?: MigrationScope;
    }) => apiAdapter.start({ childTeamId, externalTeamId, stockFile, scope }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: MIGRATION_QUERY_KEYS.children });
      queryClient.invalidateQueries({
        queryKey: MIGRATION_QUERY_KEYS.run(variables.childTeamId),
      });
    },
  });
};

export const useMigrationPause = (childTeamId: number) => {
  const queryClient = useQueryClient();
  const apiAdapter = useMemo(() => {
    const httpClient = createHttpClient(apiClientWH);
    return new MigrationApiAdapter(httpClient);
  }, []);

  return useMutation({
    mutationFn: (runId: number) => apiAdapter.pause(runId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: MIGRATION_QUERY_KEYS.children });
      queryClient.invalidateQueries({
        queryKey: MIGRATION_QUERY_KEYS.run(childTeamId),
      });
      queryClient.invalidateQueries({
        queryKey: MIGRATION_QUERY_KEYS.progress(data.data.id),
      });
    },
  });
};

export const useMigrationResume = (childTeamId: number) => {
  const queryClient = useQueryClient();
  const apiAdapter = useMemo(() => {
    const httpClient = createHttpClient(apiClientWH);
    return new MigrationApiAdapter(httpClient);
  }, []);

  return useMutation({
    mutationFn: (runId: number) => apiAdapter.resume(runId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: MIGRATION_QUERY_KEYS.children });
      queryClient.invalidateQueries({
        queryKey: MIGRATION_QUERY_KEYS.run(childTeamId),
      });
      queryClient.invalidateQueries({
        queryKey: MIGRATION_QUERY_KEYS.progress(data.data.id),
      });
    },
  });
};

export const useMigrationRetry = (runId: number) => {
  const queryClient = useQueryClient();
  const apiAdapter = useMemo(() => {
    const httpClient = createHttpClient(apiClientWH);
    return new MigrationApiAdapter(httpClient);
  }, []);

  return useMutation({
    mutationFn: (progressId: number) => apiAdapter.retry(progressId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: MIGRATION_QUERY_KEYS.progress(runId),
      });
    },
  });
};

export const useMigrationErrors = (
  runId: number | null,
  params?: { entity_type?: string; page?: number; per_page?: number },
) => {
  const apiAdapter = useMemo(() => {
    const httpClient = createHttpClient(apiClientWH);
    return new MigrationApiAdapter(httpClient);
  }, []);

  return useQuery({
    queryKey: MIGRATION_QUERY_KEYS.errors(runId ?? 0, params),
    queryFn: () => apiAdapter.getErrors(runId!, params),
    enabled: !!runId,
    staleTime: 10 * 1000,
  });
};

export const useMigrationActivity = (runId: number | null, params?: { page?: number; per_page?: number }) => {
  const apiAdapter = useMemo(() => {
    const httpClient = createHttpClient(apiClientWH);
    return new MigrationApiAdapter(httpClient);
  }, []);

  return useQuery({
    queryKey: MIGRATION_QUERY_KEYS.activity(runId ?? 0, params),
    queryFn: () => apiAdapter.getActivity(runId!, params),
    enabled: !!runId,
    staleTime: 10 * 1000,
  });
};

export const useMigrationReconciliation = () => {
  const apiAdapter = useMemo(() => {
    const httpClient = createHttpClient(apiClientWH);
    return new MigrationApiAdapter(httpClient);
  }, []);

  return useMutation({
    mutationFn: ({ runId, checks }: { runId: number; checks?: MigrationReconciliationCheckKey[] }) =>
      apiAdapter.getReconciliation(runId, checks),
  });
};

export const useMigrationDelete = (teamId: number) => {
  const queryClient = useQueryClient();
  const apiAdapter = useMemo(() => {
    const httpClient = createHttpClient(apiClientWH);
    return new MigrationApiAdapter(httpClient);
  }, []);

  return useMutation({
    mutationFn: (runId: number) => apiAdapter.deleteRun(runId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: MIGRATION_QUERY_KEYS.children });
      queryClient.invalidateQueries({
        queryKey: MIGRATION_QUERY_KEYS.run(teamId),
      });
    },
  });
};
